// Cart Manager - Handles all cart functionality
// Each store has its own isolated cart based on the shop domain
// This prevents mixing products from different stores in the same cart
(function() {
  // Get Shopify configuration from container
  const container = document.getElementById('shopify-products-container');
  const STOREFRONT_ACCESS_TOKEN = container?.dataset.storefrontToken;
  const SHOP_DOMAIN = container?.dataset.shopDomain;

  // Validate that we have a shop domain
  if (!SHOP_DOMAIN) {
    console.error('Cart Manager: SHOP_DOMAIN is required. Make sure data-shop-domain is set on #shopify-products-container');
    return;
  }

  // Create a unique cart key for this specific store
  // Example: customCart_mystore_myshopify_com
  const CART_STORAGE_KEY = `customCart_${SHOP_DOMAIN.replace(/\./g, '_')}`;
  
  console.log(`Cart Manager initialized for store: ${SHOP_DOMAIN}`);
  console.log(`Using storage key: ${CART_STORAGE_KEY}`);

  // Cart state
  let cart = {
    items: [],
    total: 0,
    shopDomain: SHOP_DOMAIN
  };

  // Format price
  function formatPrice(amount, currencyCode = 'USD') {
    return `$${parseFloat(amount).toFixed(2)}`;
  }

  // Load cart from localStorage
  function loadCart() {
    const savedCart = localStorage.getItem(CART_STORAGE_KEY);
    if (savedCart) {
      const parsed = JSON.parse(savedCart);
      // Validate that the saved cart is for this store
      if (parsed.shopDomain === SHOP_DOMAIN) {
        cart = parsed;
        updateCartUI();
      } else {
        // Cart is from a different store, start fresh
        console.warn(`Cart data found for different store (${parsed.shopDomain}). Starting fresh cart for ${SHOP_DOMAIN}.`);
        cart = {
          items: [],
          total: 0,
          shopDomain: SHOP_DOMAIN
        };
      }
    }
  }

  // Save cart to localStorage
  function saveCart() {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
  }

  // Add item to cart
  function addToCart(productData) {
    // Validate that the product belongs to this store
    if (!productData.variantId || !productData.variantId.includes('://shopify/')) {
      console.error('Invalid product data. variantId is required.');
      alert('Unable to add item to cart. Invalid product data.');
      return;
    }

    // Additional safety check: ensure we're only adding items from this store
    if (cart.items.length > 0 && cart.shopDomain !== SHOP_DOMAIN) {
      console.error('Cart domain mismatch. Clearing cart.');
      clearCart();
    }

    const existingItemIndex = cart.items.findIndex(
      item => item.variantId === productData.variantId
    );

    if (existingItemIndex > -1) {
      // Item exists, increase quantity
      cart.items[existingItemIndex].quantity += 1;
    } else {
      // New item - store the shop domain with each item for extra safety
      cart.items.push({
        variantId: productData.variantId,
        productId: productData.productId,
        title: productData.title,
        variantTitle: productData.variantTitle || null,
        price: parseFloat(productData.price),
        image: productData.image,
        handle: productData.handle,
        quantity: 1,
        shopDomain: SHOP_DOMAIN // Track which store this item belongs to
      });
    }

    calculateTotal();
    saveCart();
    updateCartUI();
    openCartDrawer();
  }

  // Remove item from cart
  function removeFromCart(variantId) {
    cart.items = cart.items.filter(item => item.variantId !== variantId);
    calculateTotal();
    saveCart();
    updateCartUI();
  }

  // Update item quantity
  function updateQuantity(variantId, newQuantity) {
    const item = cart.items.find(item => item.variantId === variantId);
    if (item) {
      if (newQuantity <= 0) {
        removeFromCart(variantId);
      } else {
        item.quantity = newQuantity;
        calculateTotal();
        saveCart();
        updateCartUI();
      }
    }
  }

  // Calculate cart total
  function calculateTotal() {
    cart.total = cart.items.reduce((sum, item) => {
      return sum + (item.price * item.quantity);
    }, 0);
  }

  // Update cart UI
  function updateCartUI() {
    const cartItemsContainer = document.querySelector('.custom-cart-items');
    const cartTotal = document.querySelector('.custom-cart-total');
    const cartEmpty = document.querySelector('.custom-cart-empty');
    const checkoutBtn = document.querySelector('.custom-cart-checkout-btn');

    if (!cartItemsContainer) return;

    if (cart.items.length === 0) {
      cartEmpty.style.display = 'block';
      cartItemsContainer.innerHTML = '';
      checkoutBtn.disabled = true;
    } else {
      cartEmpty.style.display = 'none';
      checkoutBtn.disabled = false;

      cartItemsContainer.innerHTML = cart.items.map(item => `
        <div class="custom-cart-item" data-variant-id="${item.variantId}">
          <div class="cart-item__media">
            <img class="cart-item__image" src="${item.image}" alt="${item.title}" loading="lazy">
          </div>
          <div class="custom-cart-item-details">
            <a class="cart-item__name h4 break" href="https://${SHOP_DOMAIN}/products/${item.handle}" target="_blank">${item.title}</a>
            ${item.variantTitle ? `<p class="custom-cart-item-variant">${item.variantTitle}</p>` : ''}
            <p class="custom-cart-item-price">${formatPrice(item.price)}</p>
            <div class="custom-cart-item-controls">
              <div class="quantity cart-quantity custom-cart-item-quantity">
                <button class="custom-cart-qty-btn" data-action="decrease" data-variant-id="${item.variantId}">−</button>
                <span class="quantity__input">${item.quantity}</span>
                <button class="custom-cart-qty-btn" data-action="increase" data-variant-id="${item.variantId}">+</button>
              </div>
              <button class="custom-cart-item-remove" data-variant-id="${item.variantId}">
              <span class="svg-wrapper">
                <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-remove" viewBox="0 0 16 16"><path fill="currentColor" d="M14 3h-3.53a3.07 3.07 0 0 0-.6-1.65C9.44.82 8.8.5 8 .5s-1.44.32-1.87.85A3.06 3.06 0 0 0 5.53 3H2a.5.5 0 0 0 0 1h1.25v10c0 .28.22.5.5.5h8.5a.5.5 0 0 0 .5-.5V4H14a.5.5 0 0 0 0-1M6.91 1.98c.23-.29.58-.48 1.09-.48s.85.19 1.09.48c.2.24.3.6.36 1.02h-2.9c.05-.42.17-.78.36-1.02m4.84 11.52h-7.5V4h7.5z"></path><path fill="currentColor" d="M6.55 5.25a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5m2.9 0a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5"></path></svg>
              </span>
              </button>
            </div>
          </div>
        </div>
      `).join('');
    }

    cartTotal.textContent = formatPrice(cart.total);

    // Setup event listeners for cart controls
    setupCartControls();
  }

  // Setup cart control event listeners
  function setupCartControls() {
    // Quantity buttons
    document.querySelectorAll('.custom-cart-qty-btn').forEach(btn => {
      btn.addEventListener('click', function() {
        const variantId = this.dataset.variantId;
        const action = this.dataset.action;
        const item = cart.items.find(i => i.variantId === variantId);

        if (item) {
          const newQuantity = action === 'increase' 
            ? item.quantity + 1 
            : item.quantity - 1;
          updateQuantity(variantId, newQuantity);
        }
      });
    });

    // Remove buttons
    document.querySelectorAll('.custom-cart-item-remove').forEach(btn => {
      btn.addEventListener('click', function() {
        const variantId = this.dataset.variantId;
        removeFromCart(variantId);
      });
    });
  }

  // Open cart drawer
  function openCartDrawer() {
    const drawer = document.getElementById('custom-cart-drawer');
    if (drawer) {
      drawer.classList.add('active');
      document.body.style.overflow = 'hidden';
    }
  }

  // Close cart drawer
  function closeCartDrawer() {
    const drawer = document.getElementById('custom-cart-drawer');
    if (drawer) {
      drawer.classList.remove('active');
      document.body.style.overflow = '';
    }
  }

  // Setup cart drawer controls
  function setupCartDrawer() {
    const drawer = document.getElementById('custom-cart-drawer');
    if (!drawer) {
      console.warn('Cart drawer element not found. Make sure custom-cart.liquid is included in your theme.');
      return;
    }

    // Close button
    const closeBtn = document.querySelector('.custom-cart-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', closeCartDrawer);
    }

    // Overlay click
    const overlay = document.querySelector('.custom-cart-overlay');
    if (overlay) {
      overlay.addEventListener('click', closeCartDrawer);
    }

    // Checkout button
    const checkoutBtn = document.querySelector('.custom-cart-checkout-btn');
    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', async function() {
        await createCheckoutAndRedirect();
      });
    }

    // ESC key to close
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && drawer.classList.contains('active')) {
        closeCartDrawer();
      }
    });
  }

  // Create checkout with all cart items
  async function createCheckoutAndRedirect() {
    if (cart.items.length === 0) return;

    const checkoutBtn = document.querySelector('.custom-cart-checkout-btn');
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    function restoreBtn() {
      if (checkoutBtn) {
        checkoutBtn.disabled = false;
        checkoutBtn.classList.remove('loading');
        checkoutBtn.innerHTML = 'Check out';
      }
    }

    // Show loading spinner
    if (checkoutBtn) {
      checkoutBtn.disabled = true;
      checkoutBtn.classList.add('loading');
      checkoutBtn.innerHTML = `
        <span>Check out</span>
        <div class="loading__spinner">
          <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
            <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
          </svg>
        </div>
      `;
    }

    if (!STOREFRONT_ACCESS_TOKEN || !SHOP_DOMAIN) {
      console.error('Shopify configuration missing. Make sure data attributes are set on #shopify-products-container');
      restoreBtn();
      alert('Unable to create checkout. Configuration error.');
      return;
    }

    const cartCreateMutation = `
      mutation cartCreate($input: CartInput!) {
        cartCreate(input: $input) {
          cart {
            id
            checkoutUrl
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    try {
      const lines = cart.items.map(item => ({
        merchandiseId: item.variantId,
        quantity: item.quantity
      }));

      const response = await fetch(`https://${SHOP_DOMAIN}/api/2024-01/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN
        },
        body: JSON.stringify({
          query: cartCreateMutation,
          variables: { input: { lines } }
        })
      });

      const data = await response.json();
      const checkoutUrl = data.data?.cartCreate?.cart?.checkoutUrl;

      if (checkoutUrl) {
        cart = { items: [], total: 0, shopDomain: SHOP_DOMAIN };
        saveCart();
        updateCartUI();
        closeCartDrawer();
        restoreBtn();

        if (isMobile) {
          // Mobile: same-tab redirect (popups after await get blocked)
          window.location.href = checkoutUrl;
        } else {
          // Desktop: new tab, same as the original behavior
          const w = window.open(checkoutUrl, '_blank');
          if (!w) window.location.href = checkoutUrl; // fallback if blocked
        }
      } else if (data.data?.cartCreate?.userErrors?.length > 0) {
        console.error('Cart errors:', data.data.cartCreate.userErrors);
        restoreBtn();
        alert('Error: ' + data.data.cartCreate.userErrors[0].message);
      } else if (data.errors) {
        console.error('GraphQL errors:', data.errors);
        restoreBtn();
        alert('API Error: ' + data.errors[0].message);
      } else {
        console.error('Unexpected response:', data);
        restoreBtn();
        alert('Unable to create checkout. Please try again.');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      restoreBtn();
      alert('Unable to create checkout. Please try again.');
    }
  }

  // Get cart count
  function getCartCount() {
    return cart.items.reduce((total, item) => total + item.quantity, 0);
  }

  // Get cart items
  function getCartItems() {
    return cart.items;
  }

  // Clear cart
  function clearCart() {
    cart = { items: [], total: 0, shopDomain: SHOP_DOMAIN };
    saveCart();
    updateCartUI();
  }

  // Initialize cart
  function init() {
    loadCart();
    setupCartDrawer();
    updateCartUI();
  }

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose public API scoped to this specific store
  // This prevents one store's cart from interfering with another's
  const cartAPI = {
    addToCart: addToCart,
    removeFromCart: removeFromCart,
    updateQuantity: updateQuantity,
    openCartDrawer: openCartDrawer,
    closeCartDrawer: closeCartDrawer,
    getCartCount: getCartCount,
    getCartItems: getCartItems,
    clearCart: clearCart,
    getShopDomain: () => SHOP_DOMAIN
  };

if (!window.CustomCart || typeof window.CustomCart.addToCart === 'function') {
  // If it's still a flat object, reset it to a keyed object
  window.CustomCart = {};
}

const storeKey = SHOP_DOMAIN.replace(/\./g, '_');
window.CustomCart[storeKey] = cartAPI;

})();